import { Link } from "@tanstack/react-router";
import { motion, useReducedMotion } from "framer-motion";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import type { VenueCard } from "@/api/types";
import { CoverPlaceholder } from "@/components/ui/cover-placeholder";
import { cn } from "@/lib/cn";

interface VenuePopupCardProps {
  venue: VenueCard;
  onClose: () => void;
}

function formatDistance(km: number | null): string | null {
  if (km == null) return null;
  if (km < 0.1) {
    const meters = Math.round(km * 1000);
    return `${meters} m`;
  }
  return `${km.toFixed(1)} km`;
}

function ImageCarousel({
  images,
  venue,
  onImageError,
}: {
  images: string[];
  venue: Pick<VenueCard, "slug" | "name">;
  onImageError?: (url: string) => void;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(0);
  const hasSingle = images.length <= 1;

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const onScroll = () => {
      const slideWidth = el.offsetWidth;
      if (slideWidth === 0) return;
      const newIndex = Math.min(Math.round(el.scrollLeft / slideWidth), images.length - 1);
      setIndex((prev) => (prev === newIndex ? prev : newIndex));
    };

    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [images.length]);

  const scrollTo = useCallback(
    (e: React.MouseEvent, target: number) => {
      e.preventDefault();
      e.stopPropagation();
      const el = scrollRef.current;
      if (!el) return;
      const clamped = Math.max(0, Math.min(target, images.length - 1));
      el.scrollTo({ left: clamped * el.offsetWidth, behavior: "smooth" });
    },
    [images.length],
  );

  return (
    <div className="group/carousel relative size-full">
      <Link
        to="/businesses/$slug"
        params={{ slug: venue.slug }}
        aria-label={`View ${venue.name}`}
        className="block size-full focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-electric"
        onClick={(event) => event.stopPropagation()}
      >
        <div
          ref={scrollRef}
          className="flex size-full snap-x snap-mandatory overflow-x-auto scrollbar-hide"
        >
          {images.map((src, i) => (
            <img
              key={src}
              src={src}
              alt=""
              aria-hidden="true"
              draggable={false}
              loading={i === 0 ? "eager" : "lazy"}
              className="block size-full flex-none snap-start snap-always object-cover"
              onError={onImageError ? () => onImageError(src) : undefined}
            />
          ))}
        </div>
      </Link>

      {!hasSingle && (
        <>
          <button
            type="button"
            disabled={index === 0}
            onClick={(e) => scrollTo(e, index - 1)}
            className={cn(
              "absolute left-1.5 top-1/2 flex size-7 -translate-y-1/2 items-center justify-center rounded-full bg-surface/80 text-ink opacity-0 backdrop-blur-sm transition-opacity group-hover/carousel:opacity-100 group-focus-within/carousel:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-electric",
              index === 0 && "invisible",
            )}
            aria-label="Previous image"
          >
            <ChevronLeft size={16} />
          </button>
          <button
            type="button"
            disabled={index === images.length - 1}
            onClick={(e) => scrollTo(e, index + 1)}
            className={cn(
              "absolute right-1.5 top-1/2 flex size-7 -translate-y-1/2 items-center justify-center rounded-full bg-surface/80 text-ink opacity-0 backdrop-blur-sm transition-opacity group-hover/carousel:opacity-100 group-focus-within/carousel:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-electric",
              index === images.length - 1 && "invisible",
            )}
            aria-label="Next image"
          >
            <ChevronRight size={16} />
          </button>

          <div className="absolute inset-x-0 bottom-2 flex justify-center gap-1.5">
            {images.map((_, i) => (
              <button
                key={images[i]}
                type="button"
                onClick={(e) => scrollTo(e, i)}
                className={cn(
                  "size-1.5 rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-electric focus-visible:ring-offset-2",
                  i === index ? "bg-surface" : "bg-surface/50",
                )}
                aria-label={`Image ${i + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// fallow-ignore-next-line complexity
function VenuePopupCard({ venue, onClose }: VenuePopupCardProps) {
  const prefersReducedMotion = useReducedMotion();
  const distance = formatDistance(venue.distance_km);
  const subtitle = [distance, venue.city].filter(Boolean).join(" · ");

  const [failedUrls, setFailedUrls] = useState<Set<string>>(() => new Set());
  const onImageError = useCallback((url: string) => {
    setFailedUrls((prev) => {
      if (prev.has(url)) return prev;
      const next = new Set(prev);
      next.add(url);
      return next;
    });
  }, []);

  const allImages =
    venue.gallery_urls.length > 0
      ? venue.gallery_urls
      : venue.cover_image_url
        ? [venue.cover_image_url]
        : [];
  const images = allImages.filter((url) => !failedUrls.has(url));

  return (
    <motion.div
      className="w-[280px] overflow-hidden rounded-xl bg-surface shadow-soft2"
      initial={prefersReducedMotion ? false : { opacity: 0, scale: 0.92, y: 8 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.15, ease: [0.25, 0.46, 0.45, 0.94] }}
    >
      <div className="relative aspect-[16/10] overflow-hidden bg-fog">
        {images.length > 0 ? (
          <ImageCarousel images={images} venue={venue} onImageError={onImageError} />
        ) : (
          <Link
            to="/businesses/$slug"
            params={{ slug: venue.slug }}
            aria-label={`View ${venue.name}`}
            className="block size-full focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-electric"
            onClick={(event) => event.stopPropagation()}
          >
            <CoverPlaceholder name={venue.name} decorative className="size-full" />
          </Link>
        )}

        <button
          type="button"
          className="absolute right-2 top-2 flex size-8 items-center justify-center rounded-full bg-surface/80 text-ink backdrop-blur-sm transition-colors hover:bg-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-electric"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onClose();
          }}
          aria-label="Close"
        >
          <X size={16} />
        </button>
      </div>

      <Link
        to="/businesses/$slug"
        params={{ slug: venue.slug }}
        className="flex flex-col gap-0.5 px-4 py-3 no-underline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-electric"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="font-display text-[15px] font-semibold leading-snug text-ink">
          {venue.name}
        </div>
        {subtitle && <div className="text-[13px] text-slate">{subtitle}</div>}
        {venue.primary_category_name && (
          <div className="text-[13px] text-slate">{venue.primary_category_name}</div>
        )}
      </Link>
    </motion.div>
  );
}

export { VenuePopupCard };
